import { Pipe, PipeTransform } from '@angular/core';
import { FormlyFieldConfig, FormlyTemplateOptions } from '@ngx-formly/core';

@Pipe({
  name: 'repeatTitle'
})
export class RepeatTitlePipe implements PipeTransform {
  private static cache = new Map<string, string>();
  private static readonly CACHE_SIZE = 1000;

  static clearCache(): void {
    RepeatTitlePipe.cache.clear();
  }

  transform(fallback: string, ...args: unknown[]): unknown {
    const field = args[0] as FormlyFieldConfig;
    const to = args[1] as FormlyTemplateOptions;
    const index = args[2] as number;

    if (!field || !to) {
      return fallback;
    }

    const cacheKey = this.createCacheKey(field, to, index);

    if (RepeatTitlePipe.cache.has(cacheKey)) {
      const cachedValue = RepeatTitlePipe.cache.get(cacheKey);
      return cachedValue ?? '';
    }

    const title = this._labelRepeatSection(field, to, index);

    this.setCache(cacheKey, title);

    return title;
  }

  private _labelRepeatSection(field: FormlyFieldConfig, to: FormlyTemplateOptions, index: number): string {
    const repeatGroup = this.getRepeatGroup(field, index);

    if (!repeatGroup) {
      return `${to.label} ${index + 1}`;
    }

    const title = this.generateTitle(repeatGroup);

    return title.trim() || `${to.label} ${index + 1}`;
  }

  private generateTitle(repeatGroup: FormlyFieldConfig[]): string {
    let fullNameTitle = '';
    let vehicleTitle = '';
    let addressTitle = '';
    let titleCompleted = false;

    for (const f of repeatGroup) {
      if (titleCompleted) break;

      const value = f.formControl?.value;
      const key = f.templateOptions?.dataKey;
      const type = f.type;

      if (value === null || value === undefined) continue;

      switch (type) {
        case 'firstName':
          fullNameTitle += `${value} `;
          break;
        case 'lastName':
          fullNameTitle += `${value} `;
          titleCompleted = true;
          break;
        case 'address':
          addressTitle = value;
          titleCompleted = true;
          break;
        default:
          if (key === 'vehicleYear' || key === 'vehicleMake' || key === 'vehicleModel') {
            vehicleTitle += `${value} `;
            if (key === 'vehicleModel') {
              titleCompleted = true;
            }
          }
          break;
      }
    }

    if (fullNameTitle.trim()) return fullNameTitle.trim();
    if (vehicleTitle.trim()) return vehicleTitle.trim();
    if (addressTitle) return addressTitle;

    return '';
  }

  private setCache(key: string, value: string): void {
    if (RepeatTitlePipe.cache.size >= RepeatTitlePipe.CACHE_SIZE) {
      const firstKey = RepeatTitlePipe.cache.keys().next().value;
      RepeatTitlePipe.cache.delete(firstKey);
    }

    RepeatTitlePipe.cache.set(key, value);
  }

  private createCacheKey(field: FormlyFieldConfig, to: FormlyTemplateOptions, index: number): string {
    const fieldId = field.id || '';
    const label = to.label || '';
    const fieldGroupLength = field.fieldGroup?.length || 0;
    const parentFieldGroupLength = field.parent?.fieldGroup?.length || 0;

    const fieldValues = this.getFieldValues(field, index);

    return `${fieldId}-${label}-${index}-${fieldGroupLength}-${parentFieldGroupLength}-${fieldValues}`;
  }

  private getFieldValues(field: FormlyFieldConfig, index: number): string {
    const repeatGroup = this.getRepeatGroup(field, index);
    if (!repeatGroup) return '';

    const values: string[] = [];

    for (const f of repeatGroup) {
      const value = f.formControl?.value;
      const key = f.templateOptions?.dataKey;
      const type = f.type;

      if (value !== null && value !== undefined) {
        values.push(`${key || type}:${value}`);
      }
    }

    return values.join('|');
  }

  private getRepeatGroup(field: FormlyFieldConfig, index: number): FormlyFieldConfig[] | undefined {
    if (field.fieldGroup?.length) {
      return field.fieldGroup;
    }

    if (field.parent?.fieldGroup?.[index]?.fieldGroup) {
      return field.parent.fieldGroup[index].fieldGroup;
    }

    return undefined;
  }
}
